import { useCallback } from 'react'
import { useSession } from './SessionProvider'
import { useBalanceStore } from './UserBalance'
import { MsgExecuteContractCompat, SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { BONUS_ACCOUNT_CONTRACT, XACCOUNT_CONTRACT_ADDRESS } from '@/constants/constants'
import { useAppToast } from '@/hooks/useAppToast'
import { create } from 'zustand'
import { useTRPCClient } from '@/api/trpc/context'
import BigNumber from 'bignumber.js'
import { useQuery } from '@apollo/client'
import { TOKEN_PRICES_QUERY } from '@/api/query'
import { getTokenByDenom } from '@/services/token-service'
import { calculateBankDepositAmount } from './cashierDeposit'

type CashierTransaction = 'deposit' | 'withdraw'

interface CashierState {
  isProcessing: boolean
  transaction: CashierTransaction | null
  setProcessing: (transaction: CashierTransaction) => void
  resetProcessing: () => void
}

const useCashierStore = create<CashierState>((set) => ({
  isProcessing: false,
  transaction: null,
  setProcessing: (nextTransaction) =>
    set({
      isProcessing: true,
      transaction: nextTransaction,
    }),
  resetProcessing: () =>
    set({
      isProcessing: false,
      transaction: null,
    }),
}))

export const useCashierBalance = (denom: string) => {
  const gameBalance = useBalanceStore((state) => state.balance[denom]?.game_balance)
  const walletBalance = useBalanceStore((state) => state.balance[denom]?.amount)

  return {
    gameBalance: gameBalance ?? new TokenMoney(0, denom),
    walletBalance: walletBalance ?? new TokenMoney(0, denom),
  }
}

export const useCashierFullBalance = () => {
  const balance = useBalanceStore((state) => state.balance)

  const { data: tokenPriceData } = useQuery(TOKEN_PRICES_QUERY)

  let gameBalance = new BigNumber(0)
  let walletBalance = new BigNumber(0)
  let hasGameBalance = false
  let hasGameBalancePrice = true

  for (const key in balance) {
    const tokenMetadata = getTokenByDenom(key)
    const priceValue = tokenPriceData?.tokenPrices?.nodes.find(
      (tokenPrice) => tokenPrice?.symbol.toLowerCase() === tokenMetadata.currency.toLowerCase()
    )?.value
    const parsedPrice = Number(priceValue)
    const fallbackPrice =
      tokenMetadata.currency === SupportedCurrencies.USDC || tokenMetadata.currency === SupportedCurrencies.USDT ? 1 : 0
    const price = Number.isFinite(parsedPrice) && parsedPrice > 0 ? parsedPrice : fallbackPrice
    if (balance[key]?.game_balance.chainValue.gt(0)) {
      hasGameBalance = true
      if (price === 0) hasGameBalancePrice = false
    }

    gameBalance = gameBalance.plus(
      balance[key]?.game_balance.humanBigNumber.multipliedBy(price) || 0
    )
    walletBalance = walletBalance.plus(
      balance[key]?.amount.humanBigNumber.multipliedBy(price) || 0
    )
  }

  return {
    gameBalance: gameBalance,
    walletBalance: walletBalance,
    hasGameBalance,
    hasGameBalancePrice,
  }
}

export const useCashier = () => {
  const { account, sendTransaction } = useSession()
  const { onError } = useAppToast()
  const isProcessing = useCashierStore((state) => state.isProcessing)
  const transaction = useCashierStore((state) => state.transaction)
  const setProcessing = useCashierStore((state) => state.setProcessing)
  const resetProcessing = useCashierStore((state) => state.resetProcessing)

  const canDeposit = useCallback((amount: TokenMoney, denom: string) => {
    const walletBalance = useBalanceStore.getState().balance[denom]?.amount || new TokenMoney(0, denom)
    return walletBalance.chainValue.gte(amount.chainValue)
  }, [])

  const canWithdraw = useCallback((amount?: TokenMoney, denom?: string) => {
    if (!denom) return true

    const gameBalance = useBalanceStore.getState().balance[denom]?.game_balance || new TokenMoney(0, denom)

    if (!amount) {
      return gameBalance.chainValue.gt(0)
    }

    return gameBalance.chainValue.gte(amount.chainValue)
  }, [])

  const deposit = useCallback(
    async (amount: TokenMoney, denom: string): Promise<boolean> => {
      if (!account) {
        onError()
        return false
      }

      if (!canDeposit(amount, denom)) {
        onError()
        return false
      }

      setProcessing('deposit')

      try {
        const bonusBalance = useBalanceStore.getState().balance[denom]?.bonus || new TokenMoney(0, denom)
        const bankDepositAmount = calculateBankDepositAmount(amount.chainValue, bonusBalance.chainValue)

        const msg = MsgExecuteContractCompat.fromJSON({
          sender: account.injectiveAddress,
          funds: bankDepositAmount.gt(0)
            ? [
                {
                  denom,
                  amount: bankDepositAmount.toFixed(0),
                },
              ]
            : undefined,
          contractAddress: BONUS_ACCOUNT_CONTRACT,
          msg: {
            execute: {
              contract_addr: XACCOUNT_CONTRACT_ADDRESS,
              funds: amount.toCoin(),
              contract_type: 'deposit',
              msg: Buffer.from(
                JSON.stringify({
                  deposit: {
                    player: account.injectiveAddress,
                  },
                })
              ).toString('base64'),
            },
          },
        })

        const result = await sendTransaction(msg, {
          showSuccess: true,
          showError: true,
        })

        return Boolean(result)
      } catch (error) {
        console.error('Deposit error:', error)
        onError()
        return false
      } finally {
        resetProcessing()
      }
    },
    [account, canDeposit, sendTransaction, onError, setProcessing, resetProcessing]
  )

  const trpc = useTRPCClient()

  const withdraw = useCallback(
    async (amount?: TokenMoney, denom?: string): Promise<boolean> => {
      if (!account || !denom) {
        onError()
        return false
      }

      if (!canWithdraw(amount, denom)) {
        onError()
        return false
      }

      setProcessing('withdraw')

      try {
        const payload = amount ? { denom, amount: amount.chainValue.toString() } : { denom, amount: null }
        const response = await trpc.private.games.payout.query(payload)

        return Boolean(response)
      } catch (error) {
        console.error('Withdrawal error:', error)
        onError()
        return false
      } finally {
        resetProcessing()
      }
    },
    [account, canWithdraw, onError, resetProcessing, setProcessing, trpc]
  )

  return {
    isProcessing,
    transaction,
    deposit,
    withdraw,
    canDeposit,
    canWithdraw,
  }
}
